/**
 * A host that IS one space's app.
 *
 * `app.dearcrisis.com` serves the same build as arcos.network, but the people who type it
 * want Dear Crisis's community app and nothing in the address bar about how Arcos is organised. So
 * on such a host the `/<space>/<app>` that every other URL starts with is implied by the hostname:
 * `/` is the app's Home, `/board` is its board, `/event/<ref>` is that event's popup.
 *
 * Three rules keep this from breaking links that already exist:
 *
 *   - A path that starts with the host's own space slug — `/dearcrisisnyc/community/board`,
 *     `/dearcrisisnyc/event/salon-15` — is a full Arcos path and is read as-is, so every link shared
 *     before this host existed still opens. The app then shortens the address bar (ProgramApp) so the
 *     person sees the form this host is for.
 *   - Links the app builds for its OWN space drop the prefix.
 *   - Links to another space keep the full path but become absolute to arcos.network, because on this
 *     host `/climate-week-nyc/community` would read as Dear Crisis's `climate-week-nyc` tab.
 *
 * Adding a host is one line in HOST_APPS. Nothing here reads a slug out of the hostname: the table
 * is the whole mapping, so a host is either listed or ordinary.
 */

export const CANONICAL_ORIGIN = 'https://www.arcos.network';

export const HOST_APPS = {
  'app.dearcrisis.com': { slug: 'dearcrisisnyc', app: 'community' },
};

function currentHostname() {
  try { return typeof window !== 'undefined' ? String(window.location.hostname || '') : ''; } catch { return ''; }
}

/** The `{ slug, app }` a hostname stands for, or null for an ordinary host (arcos.network, a preview, localhost). */
export function hostApp(hostname = currentHostname()) {
  return HOST_APPS[String(hostname || '').toLowerCase()] || null;
}

function firstSegment(pathname) {
  return String(pathname || '').split('/').filter(Boolean)[0] || '';
}

/**
 * The full Arcos path a browser path on this host stands for. `/board` on app.dearcrisis.com is
 * `/dearcrisisnyc/community/board`; a path already under the host's space is returned unchanged; on
 * an ordinary host every path is returned unchanged.
 */
export function fullPath(pathname, host = hostApp()) {
  const p = String(pathname || '');
  if (!host) return p;
  if (firstSegment(p) === host.slug) return p;
  const rest = p.replace(/^\/+/, '');
  return `/${host.slug}/${host.app}${rest ? `/${rest}` : ''}`;
}

/**
 * The short form this host shows for a full path under its own app — `/dearcrisisnyc/community/board`
 * becomes `/board`, the bare app path becomes `/` — or the path unchanged when it is not under the app.
 */
export function shortPath(pathname, host = hostApp()) {
  const p = String(pathname || '');
  if (!host) return p;
  const prefix = `/${host.slug}/${host.app}`;
  if (p === prefix) return '/';
  if (p.startsWith(`${prefix}/`)) return p.slice(prefix.length);
  if (p.startsWith(`${prefix}?`)) return `/${p.slice(prefix.length)}`;
  return p;
}

/**
 * Where a full Arcos path should point when it is rendered as a link on this host: its own app in the
 * short form, anything else in this space as-is (it reads correctly here), and another space as an
 * absolute arcos.network URL. On an ordinary host every path is returned unchanged.
 */
export function hostAwarePath(path, host = hostApp()) {
  if (!host || typeof path !== 'string' || !path.startsWith('/')) return path;
  const short = shortPath(path, host);
  if (short !== path) return short;
  const first = firstSegment(path);
  if (!first || first === host.slug) return path;
  return `${CANONICAL_ORIGIN}${path}`;
}
